// Admin: consolidated manual-invoice reconciliation. For every invoice
// shows current status, latest reminder stage, PayFast IDs from the most
// recent ITN event, and the latest payment outcome. Includes a per-row
// "Resend reminder" action that honours the cooldown/stage rules.

import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, RefreshCw, ClipboardList, Send, ShieldAlert, ShieldCheck } from "lucide-react";
import {
  adminListInvoiceReconciliation,
  adminResendReminderForInvoice,
} from "@/lib/api/invoice-reminders.functions";

export const Route = createFileRoute(
  "/_authenticated/portal/admin/invoice-reconciliation",
)({
  head: () => ({ meta: [{ title: "Admin · Invoice reconciliation" }] }),
  component: ReconciliationPage,
});

const rZAR = (c: number) =>
  `R${(c / 100).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function ReconciliationPage() {
  const list = useServerFn(adminListInvoiceReconciliation);
  const resend = useServerFn(adminResendReminderForInvoice);
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["admin-invoice-reconciliation"],
    queryFn: () => list(),
    refetchInterval: 60_000,
  });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"all" | "outstanding" | "paid">("all");
  const [busy, setBusy] = useState<string | null>(null);

  const rows = useMemo(() => {
    const all: any[] = data?.rows ?? [];
    const q = search.trim().toLowerCase();
    return all.filter((r) => {
      if (status === "outstanding" && (r.status === "paid" || r.status === "cancelled")) return false;
      if (status === "paid" && r.status !== "paid") return false;
      if (!q) return true;
      return [r.reference, r.client_name, r.client_email]
        .filter(Boolean)
        .some((v: string) => v.toLowerCase().includes(q));
    });
  }, [data, search, status]);

  async function onResend(ref: string) {
    setBusy(ref);
    try {
      const res: any = await resend({ data: { reference: ref } });
      if (res.sent > 0) toast.success(`Reminder sent for ${ref}`);
      else if (res.errors?.length) toast.error(`Not sent: ${res.errors.join("; ")}`);
      else toast(`Skipped (cooldown or no stage due)`, { description: `${ref}` });
      await refetch();
    } catch (e: any) {
      toast.error(e?.message ?? "Failed");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl text-navy flex items-center gap-2">
            <ClipboardList className="h-5 w-5" /> Invoice reconciliation
          </h1>
          <p className="text-sm text-muted-foreground">
            Every manual invoice with reminder stage, latest PayFast IDs, and payment outcome.
          </p>
        </div>
        <div className="flex gap-2 text-xs">
          <Link to="/portal/admin/invoice-reminder-runs" className="text-primary underline">Reminder runs →</Link>
          <Link to="/portal/admin/payfast-invoice-log" className="text-primary underline">Webhook log →</Link>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Filters</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[260px]">
              <label className="text-[11px] uppercase text-muted-foreground">Search</label>
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="reference, client or email" />
            </div>
            <div className="flex gap-1">
              {(["all", "outstanding", "paid"] as const).map((k) => (
                <Button key={k} size="sm" variant={status === k ? "default" : "outline"} onClick={() => setStatus(k)}>
                  {k[0].toUpperCase() + k.slice(1)}
                </Button>
              ))}
            </div>
            <Button size="sm" variant="ghost" onClick={() => refetch()} disabled={isFetching}>
              <RefreshCw className="mr-1 h-3 w-3" /> Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Invoices · {rows.length}</CardTitle></CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground"><Loader2 className="mr-1 inline h-4 w-4 animate-spin" /> Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No invoices match this filter.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="border-b text-left uppercase text-muted-foreground">
                  <tr>
                    <th className="py-2 pr-3">Reference</th>
                    <th className="py-2 pr-3">Client</th>
                    <th className="py-2 pr-3">Issued</th>
                    <th className="py-2 pr-3">Due</th>
                    <th className="py-2 pr-3">Total</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Reminder</th>
                    <th className="py-2 pr-3">Latest ITN</th>
                    <th className="py-2 pr-3">Payment</th>
                    <th className="py-2 pr-3">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rows.map((r: any) => {
                    const w = r.latest_webhook;
                    const canResend = r.status !== "paid" && r.status !== "cancelled";
                    return (
                      <tr key={r.id}>
                        <td className="py-1.5 pr-3 font-mono">
                          <Link to="/portal/admin/manual-invoices/$id" params={{ id: r.id }} className="text-primary underline">
                            {r.reference}
                          </Link>
                        </td>
                        <td className="py-1.5 pr-3">{r.client_name}<div className="text-[10px] text-muted-foreground">{r.client_email}</div></td>
                        <td className="py-1.5 pr-3">{r.issued_date}</td>
                        <td className="py-1.5 pr-3">{r.due_date ?? "—"}</td>
                        <td className="py-1.5 pr-3 font-medium">{rZAR(r.total_cents)}</td>
                        <td className="py-1.5 pr-3">
                          <Badge variant={r.status === "paid" ? "default" : r.status === "cancelled" ? "outline" : "secondary"}>
                            {r.status}
                          </Badge>
                        </td>
                        <td className="py-1.5 pr-3">
                          <div>Stage {r.reminder_stage ?? 0}</div>
                          <div className="text-[10px] text-muted-foreground">{r.last_reminder_sent_at ? new Date(r.last_reminder_sent_at).toLocaleString("en-ZA") : "never"}</div>
                        </td>
                        <td className="py-1.5 pr-3">
                          {w ? (
                            <div>
                              <div className="font-mono">{w.pf_payment_id ?? "—"}</div>
                              <div className="text-[10px] flex items-center gap-1 text-muted-foreground">
                                {w.signature_valid
                                  ? <span className="inline-flex items-center text-emerald-700"><ShieldCheck className="mr-0.5 h-3 w-3" />sig</span>
                                  : <span className="inline-flex items-center text-rose-700"><ShieldAlert className="mr-0.5 h-3 w-3" />sig</span>}
                                · {w.payment_status ?? "—"} · {new Date(w.created_at).toLocaleDateString("en-ZA")}
                              </div>
                            </div>
                          ) : "—"}
                        </td>
                        <td className="py-1.5 pr-3">
                          {r.paid_at ? (
                            <div>
                              <div className="text-emerald-700">paid</div>
                              <div className="text-[10px] text-muted-foreground">{new Date(r.paid_at).toLocaleDateString("en-ZA")} · {r.payment_method ?? "—"}</div>
                              <div className="text-[10px] font-mono">{r.paid_provider_ref ?? ""}</div>
                            </div>
                          ) : w?.grant_error ? (
                            <div className="text-rose-700">{w.grant_error}</div>
                          ) : "—"}
                        </td>
                        <td className="py-1.5 pr-3">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!canResend || busy === r.reference}
                            onClick={() => onResend(r.reference)}
                          >
                            {busy === r.reference ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Send className="mr-1 h-3 w-3" />}
                            Resend
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
